import { SxProps, Theme } from '@mui/material';

import { bm } from '@/theme/bausteinMasse';

/**
 * Design-System Teil B (DESIGN_SYSTEM.md, Phase 4a + Runde 3b):
 * Gemeinsame sx-Basis für MUI-TextFields auf der
 * SearchField/FilterSelect-Linie — 36px Höhe, 8px Radius, 14px
 * Schrift (Design-Rollout 25.10.2026, Freigabe Alex; vorher 32/6/13),
 * divider-Border, hover text.primary alpha ~24%, focus primary.main.
 * Multiline bleibt vom 36px-Lock ausgenommen.
 *
 * Nutzer: Filter-Datumsfelder (`filterDateSx`-Alias, Phase 4a) und
 * Dialog-Text-/Zahlfelder (Dialog-Standard, Runde 3b).
 *
 * ─── FEHLERZUSTAND (03.08.2026, Entscheid Alex) ──────────────────────────
 * Ergänzt beim Textfeld-Standard für den Buchungsdialog. Zwei Lücken, beide
 * am gerenderten DOM gemessen — nicht aus der Spezifität gerechnet:
 *
 * 1. ROTER RAHMEN FEHLTE. `theme/input.tsx` färbt den `notchedOutline` bei
 *    `Mui-error` nur im `:hover`-Zweig rot (Zeile 38-41); im Ruhezustand
 *    gewinnt die Basis-Regel `notchedOutline: { borderColor: '#D0D5DD' }`.
 *    Ein Pflichtfeld sah im Fehlerfall also aus wie ein normales Feld,
 *    solange die Maus nicht darüber stand. Betraf sieben Bestandsaufrufer
 *    (EditPoolDialog ×3, MailTemplateEditor ×2, CreateTopicPoolDialog,
 *    EditPoolMemberDialog) — die Lücke war real, nicht theoretisch.
 *
 * 2. HILFETEXT WAR MUI-DEFAULT: 14px und 14px eingerückt, während der
 *    FilterSelect-Zweig 11px ohne Einrückung liefert. Zwei kanonische
 *    Komponenten mit sichtbar verschiedenem Hilfetext — genau die Art
 *    Auseinanderlaufen, die Decision #116 beenden sollte.
 *
 * Beide Regeln stehen absichtlich HIER und nicht im Theme: ein Theme-Eingriff
 * würde jedes Eingabefeld im Projekt treffen (auch die, die den Standard nicht
 * nutzen). Der Standard trägt seine eigenen Zustände.
 */
/*
 * Die Basis als konkretes Objekt-Literal — bewusst OHNE `SxProps<Theme>`-
 * Annotation. `SxProps` ist eine Union (Objekt | Funktion | Array); ist der
 * Typ erst einmal die Union, laesst sie sich weder spreaden noch als
 * Array-Element weiterreichen (TS2322). Aus diesem Literal leiten beide
 * Exporte unten ab, damit die Werte an EINER Stelle stehen.
 */
// Hoehe, Ecke und Schrift aus `theme.bausteinMasse.eingabe` (Alt-Theme der
// Beratungsstrecke: Original-Werte 32/6/13, theme/streckeAlt).
const DS_TEXTFIELD_BASIS = {
  '& .MuiOutlinedInput-root': {
    fontSize: (t: Theme) => bm(t).eingabe.schrift,
    borderRadius: (t: Theme) => bm(t).eingabe.radius,
    bgcolor: 'background.paper',
    // Theme-Override (theme/input.tsx) lockt Single-line-Inputs per
    // `&:not(.MuiInputBase-multiline)` auf 44px (0,2,0) und gewinnt
    // Gleichstaende gegen Wrapper-sx. Gleiche :not()-Klausel hier
    // ergibt (0,3,0) — ohne !important.
    '&:not(.MuiInputBase-multiline)': { height: (t: Theme) => bm(t).eingabe.hoehe },
    // Mehrzeilig traegt MUI den Innenabstand am ROOT (`8.5px 14px` bei
    // size="small"), und darauf kam der Input-Abstand unten (6px/10px). Der
    // Text stand dadurch 14px weiter rechts als in jedem einzeiligen Feld
    // daneben (gemessen 14.10.2026 im Pflegedialog des Portals). Der
    // Abschnitt in docs/DESIGN_SYSTEM.md verlangt fuer `multiline` denselben
    // Innenabstand — er kommt jetzt allein vom Input.
    '&.MuiInputBase-multiline': { padding: 0 },
    '& fieldset': { borderColor: 'divider' },
    '&:hover fieldset': {
      borderColor: (t: Theme) => `${t.palette.text.primary}3D`,
    },
    '&.Mui-focused fieldset': {
      borderColor: 'primary.main',
      borderWidth: 1,
    },
    // Fehlerzustand schlaegt Ruhe UND Fokus: wer ein Pflichtfeld leer
    // fokussiert, soll den Fehler weiter sehen. Steht deshalb NACH der
    // Fokus-Regel (gleiche Spezifitaet, spaetere Regel gewinnt) und deckt
    // den Fokus-Fall zusaetzlich explizit ab.
    '&.Mui-error fieldset, &.Mui-error.Mui-focused fieldset': {
      borderColor: 'error.main',
    },
    // Font-Size (16px) setzt das Theme direkt auf .MuiInputBase-input
    // (0,2,0) — deshalb auch hier auf dem Input, unterhalb des
    // Root-Selektors (0,3,0).
    '& .MuiInputBase-input': {
      py: '6px',
      px: '10px',
      fontSize: (t: Theme) => bm(t).eingabe.schrift,
    },
  },
  '& .MuiInputLabel-root': {
    fontSize: 12,
  },
  // Meta/Sub-Groesse aus Teil B, identisch zum FilterSelect-Zweig. `ml: 0`,
  // weil MUI den Hilfetext sonst um 14px einrueckt und er unter dem Feld
  // verspringt. Die Fehlerfarbe kommt von MUI (`.Mui-error`) — hier wird nur
  // Groesse und Ausrichtung gesetzt, damit rot rot bleibt.
  '& .MuiFormHelperText-root': {
    fontSize: (t: Theme) => bm(t).eingabe.hinweis,
    marginLeft: 0,
    marginTop: 0.5,
  },
};

/**
 * Phase-4a-Bestandsname für Datumsfelder — identische Werte,
 * bleibt für alle bestehenden Aufrufer erhalten.
 */
export const dsTextFieldSx: SxProps<Theme> = DS_TEXTFIELD_BASIS;

export const filterDateSx: SxProps<Theme> = dsTextFieldSx;

/**
 * Mehrzeilige Textfelder auf Hausmass (Alex 10.09.2026, Rekla-BO-Kommentar).
 *
 * 🔥 WARUM `dsTextFieldSx` FUER MULTILINE NICHT REICHT — zwei Gruende, die
 * zusammenwirken:
 *
 *  1. **Keine der beiden Hoehenregeln greift.** Sowohl `dsTextFieldSx` (32px)
 *     als auch das Theme-Override (44px, `theme/input.tsx`) tragen die Klausel
 *     `&:not(.MuiInputBase-multiline)`. Das ist ABSICHT und bleibt: eine feste
 *     Hoehe an einer Textarea laesst den Text ueber die Border-Box
 *     hinauslaufen (Begruendung in `theme/input.tsx:11-17`). Folge ist aber,
 *     dass ein mehrzeiliges Feld voellig ungeregelt bleibt.
 *  2. **Das Padding sitzt woanders.** Bei `multiline` verschiebt MUI das
 *     Padding vom Input auf den **Root**. `dsTextFieldSx` setzt `py: 6px` nur
 *     auf `.MuiInputBase-input` — der Root behaelt MUIs Standardwerte, und das
 *     Feld wird deutlich hoeher als jedes andere Eingabefeld daneben.
 *
 * Diese Ergaenzung regelt deshalb nicht die HOEHE, sondern das PADDING des
 * Roots und ueberlaesst das Wachstum den `minRows`/`maxRows` des Aufrufers.
 * So bleibt die Textarea frei, die Optik aber im Hausmass.
 *
 * Anwendung: `sx={dsTextFieldMultilineSx}` — die Regel enthaelt die Basis
 * bereits, sie wird NICHT dazugemischt.
 *
 * ⚠️ WARUM NICHT KOMBINIEREN BEIM AUFRUFER: weder `{ ...dsTextFieldSx, ... }`
 * noch `sx={[dsTextFieldSx, ...]}` kompiliert. `SxProps<Theme>` ist eine
 * Union (Objekt | Funktion | Array); gespreadet ergibt sie einen Typ, den
 * `sx` nicht mehr annimmt, und als Array-ELEMENT ist sie ebenfalls
 * unzulaessig (beides TS2322, gemessen am 10.09.2026). Deshalb liefert
 * dieser Export ein fertiges sx statt eines Bausteins.
 */
export const dsTextFieldMultilineSx: SxProps<Theme> = {
  ...DS_TEXTFIELD_BASIS,
  '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
    // Gleiche Innenabstaende wie ein einzeiliges Feld — dort stehen sie auf
    // dem Input, hier muessen sie auf den Root.
    py: '6px',
    px: '10px',
    // Die Textarea bringt ihr eigenes Padding mit; doppelt gemoppelt sieht
    // aus wie ein Einzug.
    '& .MuiInputBase-inputMultiline': {
      p: 0,
      fontSize: 14,
      lineHeight: 1.4,
    },
  },
};
